import { Button } from 'components/button/Button';
import { TableStyle } from 'components/data_table/types';
import { CSSProperties } from 'react';
import styled, { css } from 'styled-components';
import { tokens } from 'styles/tokens';
import { Lookup, PickForStyled } from 'utils/types';

type CompactProps = Readonly<{
    $isCompact: boolean;
}>;

type HeightProps = PickForStyled<CSSProperties, 'height' | 'maxHeight'>;

type TableStyleProps = Readonly<{
    $tableStyle: TableStyle;
}>;

const headerCellPadding: Lookup<TableStyle, string> = {
    data: '1.6rem 2rem',
    feature: '1.6rem 2rem',
    slim: '1.6rem 2rem',
    large: '1.6rem 2rem',
};

const dataCellHeights: Lookup<TableStyle, string> = {
    data: '6rem',
    large: '8rem',
    feature: '4.5rem',
    slim: '3rem',
};

export const TableWrapper = styled.div<CompactProps & HeightProps>`
    border: solid 1px ${tokens.border.default};
    border-radius: 0.8rem;
    max-height: ${({ $maxHeight }) => $maxHeight ?? 'none'};
    height: ${({ $height }) => $height ?? 'auto'};
    overflow-y: ${({ $height, $maxHeight }) =>
        $height || $maxHeight ? 'auto' : 'hidden'};
    ${({ $isCompact }) =>
        $isCompact
            ? ''
            : css`
                  position: relative;
                  overflow-x: auto;
              `}
    margin-bottom: 1rem;
`;

export const BaseTable = styled.table`
    width: 100%;
    font-size: 1.4rem;
    box-sizing: border-box;
    background: ${tokens.background.paperElevation2};
    color: ${tokens.text.primary};
`;

export const StyledTable = styled(BaseTable)<CompactProps & TableStyleProps>`
    box-shadow: ${tokens.elevation['100']};
    border-collapse: separate;
    border-spacing: 0;

    ${({ $tableStyle, $isCompact }) =>
        $tableStyle === 'slim' && !$isCompact
            ? css`
                  tbody tr:nth-child(odd) td {
                      background: ${tokens.background.paperElevation4};
                  }
              `
            : ''}
`;

export const StyledHeaderCell = styled.th<TableStyleProps>`
    text-align: left;
    padding: ${({ $tableStyle }) => headerCellPadding[$tableStyle]};
    font-size: 1.4rem;
    color: ${tokens.text.secondary};
    background: ${tokens.background.paperElevation2};
    white-space: nowrap;
    border-bottom: solid 1px ${tokens.border.default};
`;

const BaseCell = styled.td`
    vertical-align: middle;
    background: ${tokens.background.paperElevation2};
    padding: 0 2rem;
    border-top: solid 1px ${tokens.border.default};

    tbody > tr:first-child > & {
        border-top: none;
    }
`;

export const FullDataCell = styled(BaseCell)`
    &:not(:first-child) {
        border-left: solid 1px ${tokens.border.default};
    }
`;

export const CellContent = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: center;
    flex-grow: 1;

    & > * {
        flex-grow: 1;
    }
`;

export const FullDataCellContent = styled(CellContent)<TableStyleProps>`
    height: ${({ $tableStyle }) => dataCellHeights[$tableStyle]};
    overflow: hidden;
`;

export const CompactPrimaryCell = styled(BaseCell)``;

export const CompactPrimaryCellContent = styled(CellContent)<TableStyleProps>`
    height: ${({ $tableStyle }) => dataCellHeights[$tableStyle]};
`;

export const CompactSecondaryCell = styled.td`
    padding: 0;
    border-top: solid 1px ${tokens.border.default};
`;

export const CompactSecondaryTable = styled(BaseTable)<TableStyleProps>`
    border-collapse: separate;
    border-spacing: 0;

    td {
        vertical-align: middle;
        height: ${({ $tableStyle }) => dataCellHeights[$tableStyle]};
        padding: 0 2rem;
    }

    td:not(:first-child) {
        border-left: solid 1px ${tokens.border.default};
    }

    tr:not(:first-child) td {
        border-top: solid 1px ${tokens.border.default};
    }
`;

export const CompactButton = styled(Button).attrs({ type: 'button' })`
    margin-left: 2rem;
`;

export const StyledTableFoot = styled.tfoot<CompactProps>`
    ${({ $isCompact }) =>
        $isCompact
            ? ''
            : css`
                  font-size: 1.6rem;
              `}
`;
